import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Field, change } from 'redux-form';
import { t } from 'i18next';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import { AddressBookEntry } from '../AddressBookEntry';
import {
  isEmail, isPhone, isRequired, maxChar40, maxChar50, originalShipmentOrQuoteValidator, RMA, isBillToDigitLength10,
} from '../../../utils/validator';
import { phoneNumber } from '../../../utils/mask';
import { arrayLengthSafe } from '../../../utils/arrays';
import { isCanadianAddress, isAmericanAddress } from '../../../utils/func';
import {
  FieldGroupLabel,
  PaymentMethod,
  PaymentMethodsContainer,
  AddressBlock,
  GeneralContainer,
  BillOfLaddingRadioContainer,
  UseQuoteOrShipmentNumberContainer,
  BillingAccountsContainer,
  BillToGuide,
  ErrorMessage,
  ErrorIcon,
} from '../css';
import {
  AddressTypes,
  Divisions,
  PaymentMethods,
  SamedayShipmentType,
  IsYesOrNo,
} from '../../../utils/enums';
import { billingAccountTextFormatter, billingAccountToNumber, normalizeBoolean } from '../../../utils/form';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import PrimaryButton from '../../../lib/ui/buttons/PrimaryButton';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';
import RadioButton from '../../../lib/ui/form_elements/RadioButton';
import AvoidChangesMask from '../../../lib/ui/form_elements/AvoidChangesMask/AvoidChangesMask';

const FormName = 'CreateShipmentForm';

const renderRadioControl = (name, title, disabled, formData) => {
  let selected = true;

  if (formData) {
    selected = (formData[name] && formData[name] === true);
  }

  let options = [
    { label: t(IsYesOrNo.Yes), value: true, defaultChecked: selected },
    { label: t(IsYesOrNo.No), value: false, defaultChecked: !selected },
  ];

  if (name === 'billToDayAndRoss') {
    options = [
      { label: t('DayAndRoss'), value: true, defaultChecked: selected },
      { label: t('RL'), value: false, defaultChecked: !selected },
    ];
  }

  return (
    <>
      <FieldGroupLabel>
        {title}
      </FieldGroupLabel>
      <BillOfLaddingRadioContainer>
        <Field
          name={name}
          component={RadioButton}
          options={options}
          normalize={normalizeBoolean}
          disabled={disabled}
        />
      </BillOfLaddingRadioContainer>
    </>
  );
};

const Step1 = ({
  step,
  formData = {},
  dispatch,
  loggedIn,
  handleExistingQuoteOrShipment,
  handleBillTo,
  quoteNumberError,
  parentFields = {},
  billingAccounts = [],
  fromContactId,
  toContactId,
  isLoggedIn,
  addressFieldsRef,
  openContactPermissionModal,
  fieldsShouldBeBlocked,
  incompleteAddresses,
  addressValidation,
  avoidChanges,
}) => {
  const [isAddNewBillingAddress, setIsAddNewBillingAddress] = useState(false);
  const prevPaymentMethodRef = useRef(undefined);

  useEffect(() => {
    if (handleBillTo) {
      const hasAccountInList = billingAccounts.find((item) => item.accountNumber === handleBillTo);
      if (!hasAccountInList) {
        setIsAddNewBillingAddress(true);
      }

      dispatch(change(FormName, 'billTo', handleBillTo));
    }
  }, [handleBillTo]);

  const {
    division, createBillOfLading, paymentMethod, shipmentType, billToDayAndRoss, billTo,
  } = formData;

  useEffect(() => {
    if (!handleBillTo) {
      prevPaymentMethodRef.current = paymentMethod;
      return;
    }

    if (paymentMethod === PaymentMethods.CreditCard) {
      if (billTo !== handleBillTo) {
        dispatch(change(FormName, 'billTo', handleBillTo));
      }

      prevPaymentMethodRef.current = paymentMethod;
      return;
    }

    if (prevPaymentMethodRef.current === PaymentMethods.CreditCard && billTo === handleBillTo) {
      dispatch(change(FormName, 'billTo', ''));
      setIsAddNewBillingAddress(true);
    }

    prevPaymentMethodRef.current = paymentMethod;
  }, [paymentMethod, handleBillTo, billTo, dispatch]);

  const hasSingularPrepaidAccount = isLoggedIn
  && paymentMethod
  && paymentMethod.name === PaymentMethods.Prepaid
  && arrayLengthSafe(billingAccounts) === 1;

  const renderBillingInformation = () => {
    if (paymentMethod === PaymentMethods.CreditCard) {
      return undefined;
    }

    const hideDropdown = fieldsShouldBeBlocked && handleBillTo;

    return (
      <Row>
        <Column>
          <FieldGroupLabel>{parentFields.categoryTitleStep1 && parentFields.categoryTitleStep1.value}</FieldGroupLabel>
          <BillingAccountsContainer>
            {!hideDropdown && (
            <Row>
              <Column $tablet={6} $mobile={12}>
                <Field
                  name="billTo"
                  label={t('BillTo')}
                  component={Dropdown}
                  options={billingAccounts.map((item) => ({ ...item, value: billingAccountTextFormatter(item) }))}
                  validate={[isRequired]}
                  normalize={billingAccountToNumber}
                  valueField="accountNumber"
                  valueFormatter={billingAccountTextFormatter}
                  preOption={{
                    isOptionActive: isAddNewBillingAddress,
                    handleOptionClick: () => setIsAddNewBillingAddress(true),
                    handleOtherOptionsClick: () => setIsAddNewBillingAddress(false),
                    placeholder: t('AddNewOption'),
                  }}
                />
                {isAddNewBillingAddress && (
                  <div style={{ marginTop: '16px' }}>
                    <Field
                      name="billTo"
                      label={t('BillTo')}
                      component={TextInput}
                      validate={[isRequired, isBillToDigitLength10]}
                    />
                  </div>
                )}
                <FieldGroupLabel>
                  {parentFields.categoryTitle2Step1 && parentFields.categoryTitle2Step1.value}
                </FieldGroupLabel>
                <Field
                  name="quoteDest"
                  component={TextInput}
                  label={t('ContactEmail')}
                  validate={[isEmail, maxChar50, isRequired]}
                />
              </Column>
              {loggedIn && (
                <Column $tablet={6} $mobile={12}>
                  <BillToGuide>
                    <h4>{t('BillToGuideTitle')}</h4>
                    <p>{t('BillToGuideFormat')}</p>
                    <dl>
                      <dt>{t('BillToGuideServiceLabel')}</dt>
                      <dd>{t('BillToGuideServiceValue')}</dd>
                      <dt>{t('BillToGuideCurrencyLabel')}</dt>
                      <dd>{t('BillToGuideCurrencyValue')}</dd>
                      <dt>{t('BillToGuideUsageLabel')}</dt>
                      <dd>{t('BillToGuideUsageValue')}</dd>
                    </dl>
                  </BillToGuide>
                </Column>
              )}
            </Row>
            )}
            {hideDropdown && (
              <Row>
                <Column $tablet={6} $mobile={12}>
                  <AvoidChangesMask active onClick={() => avoidChanges(t('BillTo'))} />
                  <Field
                    name="billTo"
                    label={t('BillTo')}
                    component={TextInput}
                  />
                </Column>
              </Row>
            )}
          </BillingAccountsContainer>
        </Column>
      </Row>
    );
  };

  if (hasSingularPrepaidAccount) {
    dispatch(change(FormName, 'billTo', billingAccounts[0].accountNumber));
  }

  const isFromQuote = !!(formData && formData.quoteOrShipmentNumber && formData.quoteOrShipmentNumber.toString().toUpperCase().includes('Q'));

  const paymentMethods = useMemo(() => {
    // Unauthenticated shipment from quote: only show Credit Card
    if (!loggedIn && isFromQuote) {
      return [
        { label: t('PaymentCreditCard'), subLabel: t('PaymentCreditCardDesc'), value: PaymentMethods.CreditCard },
      ];
    }

    // Unauthenticated: only Credit Card and Other
    if (!loggedIn) {
      return [
        { label: t('PaymentCreditCard'), subLabel: t('PaymentCreditCardDesc'), value: PaymentMethods.CreditCard },
        { label: t('PaymentOther'), subLabel: t('PaymentOtherDesc'), value: PaymentMethods.Other },
      ];
    }

    return [];
  }, [loggedIn, isFromQuote]);

  const populateFormFromOrderOrQuote = (e) => {
    e.preventDefault();
    const type = (shipmentType === 'Regular') ? 'fromForm' : 'returning';
    handleExistingQuoteOrShipment(type);
  };

  const getShipmentOrQuoteMaxLength = (value = '') => {
    if (value.startsWith('A')) {
      return 10; // A + 9 digits
    }

    if (value.startsWith('WQ')) {
      return 9; // WQ + 8 digits
    }

    if (value.startsWith('Q')) {
      return 9; // Q + 8 digits
    }

    if (value.startsWith('US')) {
      return 11; // Q + 6 digits
    }

    return 10;
  };

  if (step === 0) {
    return (
      <>
        <Row>
          <Column $tablet={6} $mobile={12} className="my-5">
            <Field
              name="shipmentType"
              label={t('SelectShipmentType')}
              component={Dropdown}
              options={Object.values(SamedayShipmentType).map((item) => ({ value: item }))}
              validate={[isRequired]}
              disabled={(formData.quoteOrShipmentNumber && !fieldsShouldBeBlocked)}
              specialLabel
            />
          </Column>
        </Row>

        <Row>
          <Column $tablet={6} $mobile={12} className="mb-5">
            <UseQuoteOrShipmentNumberContainer>
              <div>
                <Field
                  name="quoteOrShipmentNumber"
                  component={TextInput}
                  label={(shipmentType === 'Regular') ? t('QuoteOrShipmentNumber') : t('OriginalShipment')}
                  validate={[originalShipmentOrQuoteValidator]}
                  optional
                  maxLength={getShipmentOrQuoteMaxLength(formData?.quoteOrShipmentNumber)}
                />
                <div className="mt-auto" style={{ paddingTop: '10px' }}>
                  <PrimaryButton
                    label={t('Apply')}
                    onClick={(e) => populateFormFromOrderOrQuote(e)}
                  />
                </div>
              </div>
              <div>
                {quoteNumberError && (
                <ErrorMessage>
                  <ErrorIcon />
                  {quoteNumberError}
                </ErrorMessage>
                )}
              </div>
            </UseQuoteOrShipmentNumberContainer>
          </Column>
        </Row>

        {
          (shipmentType !== 'Regular')
            ? (
              <Row>
                <Column $tablet={6} $mobile={12}>
                  <Field
                    name="returnAuthorization"
                    component={TextInput}
                    label={t('ReturnAuthorization')}
                    validate={[isRequired, RMA, maxChar40]}
                    maxLength={40}
                  />
                </Column>
              </Row>
            )
            : null
        }

        {renderRadioControl('createBillOfLading', parentFields.billOfLadingSummary
          && parentFields.billOfLadingSummary.value, (division === Divisions.Sameday.name), formData)}

        <Row ref={addressFieldsRef}>
          <AddressBlock className="col-md-6">
            <FieldGroupLabel>
              {t('ShipperDetailsTitle')}
            </FieldGroupLabel>
            <AddressBookEntry
              contactType={AddressTypes.SHIPPER}
              formName="CreateShipmentForm"
              defaultContactId={fromContactId}
              isAddressRequired
              openContactPermissionModal={openContactPermissionModal}
              showResidentialAddressCheckbox
              fieldsShouldBeBlocked={fieldsShouldBeBlocked}
              incompleteAddresses={incompleteAddresses}
              avoidChanges={avoidChanges}
              isFromQuote={isFromQuote}
            />
          </AddressBlock>
          <AddressBlock className="col-md-6">
            <FieldGroupLabel>
              {t('ConsigneeDetailsTitle')}
            </FieldGroupLabel>
            <AddressBookEntry
              contactType={AddressTypes.CONSIGNEE}
              formName="CreateShipmentForm"
              defaultContactId={toContactId}
              isAllFieldsOptional={!createBillOfLading}
              isAddressRequired
              openContactPermissionModal={openContactPermissionModal}
              showResidentialAddressCheckbox
              showConsigneeInfoCheckbox
              fieldsShouldBeBlocked={fieldsShouldBeBlocked}
              avoidChanges={avoidChanges}
              incompleteAddresses={incompleteAddresses}
              addressValidation={addressValidation}
              isFromQuote={isFromQuote}
            />
          </AddressBlock>
        </Row>
        {
          formData && formData.shipperAddress && formData.consigneeAddress && ((
            (isAmericanAddress(formData.shipperAddress) && isCanadianAddress(formData.consigneeAddress))
            || (isCanadianAddress(formData.shipperAddress) && isAmericanAddress(formData.consigneeAddress)))
            || (formData.shipperCountry && formData.consigneeCountry && (
              (isAmericanAddress(formData.shipperCountry) && isCanadianAddress(formData.consigneeCountry))
              || (isCanadianAddress(formData.shipperCountry) && isAmericanAddress(formData.consigneeCountry)))
            ))
          && (
            <Row>
              <Column>
                <FieldGroupLabel>
                  {parentFields.categoryTitle3Step1 && parentFields.categoryTitle3Step1.value}
                </FieldGroupLabel>
                <GeneralContainer>
                  <Field
                    name="broker.name"
                    component={TextInput}
                    label={t('BrokerName')}
                    validate={[maxChar50, isRequired]}
                  />
                  <Field
                    name="broker.emailAddress"
                    component={TextInput}
                    label={t('BrokerEmailAddress')}
                    validate={[isEmail, maxChar50, isRequired]}
                  />
                  <Field
                    name="broker.phoneNumber"
                    component={TextInput}
                    label={t('BrokerPhoneNumber')}
                    validate={[isPhone, maxChar50, isRequired]}
                    normalize={phoneNumber}
                  />
                </GeneralContainer>
              </Column>
            </Row>
          )
        }

        <PaymentMethod>
          {!loggedIn && (
            <>
              <FieldGroupLabel>
                {t('PaymentMethod')}
              </FieldGroupLabel>
              <PaymentMethodsContainer loggedIn={loggedIn}>
                <Field
                  name="paymentMethod"
                  component={RadioButton}
                  options={paymentMethods}
                  checkPayment={formData.paymentMethod}
                />
              </PaymentMethodsContainer>
            </>
          )}

          {
            formData && formData.shipperAddress && formData.consigneeAddress && (
              (isAmericanAddress(formData.shipperAddress) && isCanadianAddress(formData.consigneeAddress))
              || (isCanadianAddress(formData.shipperAddress) && isAmericanAddress(formData.consigneeAddress))
            ) && (
              <Row className="mt-3">
                <Column>
                  {renderRadioControl('billToDayAndRoss', t('IsTheBillDayRossOrRL'), false, formData)}
                </Column>
              </Row>
            )
          }

          {loggedIn ? renderBillingInformation() : (paymentMethod && billToDayAndRoss && renderBillingInformation())}

          {(!loggedIn && (!paymentMethod || !billToDayAndRoss || paymentMethod === PaymentMethods.CreditCard)) && (
            <Row>
              <Column $tablet={6} $mobile={12}>
                <FieldGroupLabel>
                  {parentFields.categoryTitle2Step1 && parentFields.categoryTitle2Step1.value}
                </FieldGroupLabel>
                <Field
                  name="quoteDest"
                  component={TextInput}
                  label={t('ContactEmail')}
                  validate={[isEmail, maxChar50, isRequired]}
                />
              </Column>
            </Row>
          )}
        </PaymentMethod>
      </>
    );
  }

  return null;
};

Step1.propTypes = {
  step: PropTypes.number,
  formData: PropTypes.object,
  dispatch: PropTypes.func,
  loggedIn: PropTypes.bool,
  handleExistingQuoteOrShipment: PropTypes.func,
  quoteNumberError: PropTypes.string,
  parentFields: PropTypes.object,
  billingAccounts: PropTypes.array,
  fromContactId: PropTypes.number,
  toContactId: PropTypes.number,
  isLoggedIn: PropTypes.bool,
  handleBillTo: PropTypes.string,
  addressFieldsRef: PropTypes.object,
  openContactPermissionModal: PropTypes.func,
  fieldsShouldBeBlocked: PropTypes.bool,
  addressValidation: PropTypes.bool,
  avoidChanges: PropTypes.func,
  incompleteAddresses: PropTypes.object,
};

export default Step1;
